import { getCalling, getTown } from '@wyn/content';
import {
  type AdminEvent,
  type AdminLedgerRow,
  type AdminPlayer,
  type AdminPlayerResponse,
  type AdminReportSummary,
  type AdminReportsResponse,
  type ChatMessageView,
  type ModlogEntry,
  REPORT_REASON_LABELS,
  SANCTION_DURATION_LABELS,
  SANCTION_DURATIONS,
  type SanctionDuration,
} from '@wyn/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { Masthead } from '../components/Layout.tsx';
import { Icon } from '../components/Icon.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { sanctionUntil } from '../components/Sanction.tsx';
import { Callout, PageTitle } from '../components/ui.tsx';
import { UI } from '../icons/ui-icons.ts';
import { adminBetweenQuery, adminFlagsQuery, adminPlayerQuery, adminRealmQuery, adminReportsQuery, useAdminAction } from '../lib/queries.ts';
import { rt } from '../lib/runtime.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

type Report = AdminReportsResponse['reports'][number];
type Tab = 'queue' | 'player' | 'flags' | 'realm';

const when = (at: number) => new Date(at).toLocaleString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const ACTION_LABELS: Record<ModlogEntry['action'], string> = {
  'remove-line': 'Line removed',
  mute: 'Muted',
  ban: 'Banned',
  lift: 'Lifted',
  rename: 'Rename forced',
  resolve: 'Report resolved',
};

/**
 * The stewards' view: open reports oldest first, a player lookup, the shared-device trade flags, and
 * the realm's coin by day.
 * The API refuses anyone outside the `admins` group; this page only saves them the trip.
 */
export function Admin() {
  const me = useMe();
  const [tab, setTab] = useState<Tab>('queue');
  const [lookup, setLookup] = useState('');
  if (me.isPending) return <Loading />;
  const shell = (children: ReactNode) => (
    <div className="mx-auto flex min-h-dvh max-w-5xl flex-col px-4">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule py-3">
        <Masthead />
        <nav className="flex gap-4 text-sm">
          {me.data?.player && <Link to="/town" className="nav-link">Back to the game</Link>}
          <button type="button" className="text-ink-faint hover:text-ink" onClick={() => rt().auth.signOut()}>Sign out</button>
        </nav>
      </header>
      <main className="space-y-4 py-6">{children}</main>
    </div>
  );
  if (!me.data?.isAdmin)
    return shell(
      <section className="panel space-y-2">
        <PageTitle icon={UI.locked}>Stewards only</PageTitle>
        <p>Only the stewards may come here.</p>
      </section>,
    );
  const look = (q: string) => {
    setLookup(q);
    setTab('player');
  };
  const tabs: [Tab, string][] = [['queue', 'Reports'], ['player', 'Player'], ['flags', 'Trade flags'], ['realm', 'Realm']];
  return shell(
    <>
      <PageTitle icon={UI.stewards}>The stewards</PageTitle>
      <div role="tablist" aria-label="Stewards" className="flex gap-2">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? 'btn' : 'btn-quiet'} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>
      {tab === 'queue' && <Queue onLook={look} />}
      {tab === 'player' && <PlayerLookup key={lookup} initial={lookup} />}
      {tab === 'flags' && <Flags onLook={look} />}
      {tab === 'realm' && <Realm />}
    </>,
  );
}

function Queue({ onLook }: { onLook: (q: string) => void }) {
  const q = useQuery(adminReportsQuery());
  if (q.isPending) return <p className="text-ink-faint">Reading the reports…</p>;
  if (q.error) return <ErrorNotice error={q.error} />;
  const { reports } = q.data;
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-faint">{reports.length === 0 ? 'No reports are waiting.' : `${reports.length} waiting, oldest first.`}</p>
      {reports.map((r) => <ReportCard key={r.report.id} item={r} onLook={onLook} />)}
    </div>
  );
}

function ReportCard({ item, onLook }: { item: Report; onLook: (q: string) => void }) {
  const { report, target, openAgainst, history } = item;
  const resolve = useAdminAction('adminResolve');
  const [note, setNote] = useState('');
  const removedHere = history.some((e) => e.action === 'remove-line' && report.chat && e.detail.lineId === report.chat.line.id);
  return (
    <article className="panel space-y-3" aria-label={`Report on ${report.targetName}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl">
          {REPORT_REASON_LABELS[report.reason]} · <button type="button" className="text-ochre underline" onClick={() => onLook(report.targetId)}>{target?.name ?? report.targetName}</button>
          {target && target.name !== report.targetName && <span className="text-sm text-ink-faint"> (reported as {report.targetName})</span>}
        </h2>
        <span className="text-sm text-ink-faint">
          {when(report.at)} · by <button type="button" className="text-ochre underline" onClick={() => onLook(report.reporterId)}>{report.reporterName}</button>
        </span>
      </header>
      {report.note && <blockquote className="border-l-2 border-rule pl-3 text-ink-dim">{report.note}</blockquote>}
      {target ? <Standing player={target} openAgainst={openAgainst} /> : <Callout tone="bad">The player row has gone.</Callout>}
      {report.chat && (
        <div className="space-y-2">
          <h3 className="smallcaps text-sm text-ink-dim">In {report.chat.channel === 'realm' ? 'the Realm' : getTown(report.chat.channel.slice('town:'.length) as never).name}</h3>
          <ol aria-label="What was said" className="well space-y-1 p-3 text-sm">
            {[...report.chat.before, report.chat.line, ...report.chat.after].map((m) => (
              <ChatLine key={m.id} line={m} reported={m.id === report.chat!.line.id} />
            ))}
          </ol>
          {target && !removedHere && <RemoveLine line={report.chat.line} reportId={report.id} label="Remove the reported line" reason={REPORT_REASON_LABELS[report.reason]} />}
          {removedHere && <p className="text-sm text-ink-faint">The reported line has been removed.</p>}
        </div>
      )}
      <Between a={{ id: report.reporterId, name: report.reporterName }} b={{ id: report.targetId, name: target?.name ?? report.targetName }} onLook={onLook} />
      {target && <Actions player={target} reportId={report.id} />}
      {history.length > 0 && <History entries={history} />}
      <form className="flex flex-wrap items-center gap-2 border-t border-rule pt-3" onSubmit={(e) => e.preventDefault()}>
        <input className="field min-w-0 flex-1" aria-label="Note on the outcome" placeholder="Note on the outcome (optional)" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
        <button type="button" className="btn" disabled={resolve.isPending} onClick={() => resolve.mutate({ reportId: report.id, outcome: 'actioned', note })}>Resolve: actioned</button>
        <button type="button" className="btn-quiet" disabled={resolve.isPending} onClick={() => resolve.mutate({ reportId: report.id, outcome: 'no-action', note })}>Resolve: no action</button>
      </form>
      <ErrorNotice error={resolve.error} />
    </article>
  );
}

function ChatLine({ line, reported = false, children }: { line: ChatMessageView; reported?: boolean; children?: ReactNode }) {
  return (
    <li className={`grid grid-cols-[6.5rem_1fr_auto] gap-2 ${reported ? 'bg-blood/10 outline outline-1 outline-blood' : ''}`} aria-current={reported || undefined}>
      <time className="font-mono text-xs leading-5 text-ink-faint" dateTime={new Date(line.at).toISOString()}>{when(line.at)}</time>
      <span className="break-words">
        <span className="text-ochre">{line.name}</span>
        <span className="text-ink-faint">: </span>
        {line.removed ? <span className="italic text-ink-faint">— removed by a steward —</span> : line.text}
      </span>
      {children}
    </li>
  );
}

/** Who the player is and what holds them now. */
function Standing({ player, openAgainst }: { player: AdminPlayer; openAgainst?: number }) {
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
      <div><dt className="inline text-ink-faint">Player </dt><dd className="inline">{player.name} · {getCalling(player.calling).name} · {getTown(player.town).name}{player.resident && ' · resident'}</dd></div>
      <div><dt className="inline text-ink-faint">Since </dt><dd className="inline">{when(player.createdAt)}</dd></div>
      <div>
        <dt className="inline text-ink-faint">Sanction </dt>
        <dd className="inline">{player.sanction ? <span className="text-blood">{player.sanction.kind === 'mute' ? 'Muted' : 'Banned'} {sanctionUntil(player.sanction)}</span> : 'none'}</dd>
      </div>
      {player.renameRequired && <div className="text-blood">Waiting to choose a new name</div>}
      {openAgainst !== undefined && <div><dt className="inline text-ink-faint">Open reports </dt><dd className="inline">{openAgainst}</dd></div>}
    </dl>
  );
}

function RemoveLine({ line, reportId, label, reason = 'Removed by a steward.' }: { line: ChatMessageView; reportId?: string; label: string; reason?: string }) {
  const remove = useAdminAction('adminRemoveLine');
  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" className="btn-quiet py-0 text-sm" disabled={remove.isPending} onClick={() => remove.mutate({ channel: line.channel, lineId: line.id, reason, reportId })}>
        {label}
      </button>
      <ErrorNotice error={remove.error} />
    </span>
  );
}

/** Mute, ban, lift or force a rename, with a reason the player sees. */
function Actions({ player, reportId }: { player: AdminPlayer; reportId?: string }) {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState<SanctionDuration>('24h');
  const sanction = useAdminAction('adminSanction');
  const lift = useAdminAction('adminLift');
  const rename = useAdminAction('adminRename');
  const busy = sanction.isPending || lift.isPending || rename.isPending;
  const ready = reason.trim().length > 0 && !busy;
  const base = { playerId: player.id, reason, reportId };
  return (
    <fieldset className="space-y-2" aria-label={`Act on ${player.name}`}>
      <legend className="smallcaps text-sm text-ink-dim">Act on {player.name}</legend>
      <input className="field w-full" aria-label="Reason the player sees" placeholder="Reason the player sees" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="flex flex-wrap items-center gap-2">
        <select className="field" aria-label="For how long" value={duration} onChange={(e) => setDuration(e.target.value as SanctionDuration)}>
          {SANCTION_DURATIONS.map((d) => <option key={d} value={d}>{SANCTION_DURATION_LABELS[d]}</option>)}
        </select>
        <button type="button" className="btn" disabled={!ready} onClick={() => sanction.mutate({ ...base, kind: 'mute', duration })}>Mute</button>
        <button type="button" className="btn" disabled={!ready} onClick={() => sanction.mutate({ ...base, kind: 'ban', duration })}>Ban</button>
        {player.sanction && <button type="button" className="btn-quiet" disabled={!ready} onClick={() => lift.mutate(base)}>Lift the {player.sanction.kind}</button>}
        {!player.renameRequired && <button type="button" className="btn-quiet" disabled={!ready} onClick={() => rename.mutate(base)}>Force a rename</button>}
      </div>
      <ErrorNotice error={sanction.error ?? lift.error ?? rename.error} />
    </fieldset>
  );
}

function History({ entries }: { entries: ModlogEntry[] }) {
  return (
    <details open={entries.length <= 5}>
      <summary className="cursor-pointer smallcaps text-sm text-ink-dim">Moderation log ({entries.length})</summary>
      <ol aria-label="Moderation log" className="mt-2 space-y-1 text-sm">
        {entries.map((e) => (
          <li key={e.id} className="grid grid-cols-[6.5rem_1fr] gap-2">
            <time className="font-mono text-xs leading-5 text-ink-faint">{when(e.at)}</time>
            <span>
              <span className="text-ochre">{ACTION_LABELS[e.action]}</span>
              {typeof e.detail.until === 'number' && ` until ${when(e.detail.until)}`}
              {e.detail.until === null && ' for good'}
              {e.action === 'resolve' && ` (${e.detail.outcome === 'actioned' ? 'actioned' : 'no action'})`}
              {' · '}{e.reason}
              {typeof e.detail.text === 'string' && <span className="block text-ink-faint">“{e.detail.text}”</span>}
              {typeof e.detail.name === 'string' && <span className="text-ink-faint"> (was {e.detail.name})</span>}
            </span>
          </li>
        ))}
      </ol>
    </details>
  );
}

function PlayerLookup({ initial }: { initial: string }) {
  const [text, setText] = useState(initial);
  const [q, setQ] = useState(initial);
  const look = (id: string) => {
    setText(id);
    setQ(id);
  };
  const r = useQuery(adminPlayerQuery(q));
  // Once found, follow the player by id: a forced rename releases the name they were looked up by.
  const foundId = r.data?.player.id;
  useEffect(() => {
    if (foundId && q !== foundId) setQ(foundId);
  }, [foundId, q]);
  return (
    <section className="space-y-4">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setQ(text.trim()); }}>
        <input className="field min-w-0 flex-1" aria-label="Name or id" placeholder="A player’s name (or id)" value={text} onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="btn" disabled={!text.trim()}>Look up</button>
      </form>
      {q && r.isPending && <p className="text-ink-faint">Looking…</p>}
      <ErrorNotice error={r.error} />
      {r.data && (
        <article className="panel space-y-3" aria-label={`Player ${r.data.player.name}`}>
          <h2 className="text-xl">{r.data.player.name}</h2>
          <Standing player={r.data.player} />
          <Actions player={r.data.player} />
          <History entries={r.data.modlog} />
          <div className="space-y-2">
            <h3 className="smallcaps text-sm text-ink-dim">Recent chat</h3>
            {r.data.chat.length === 0 ? (
              <p className="text-sm text-ink-faint">Nothing in chat in the last 30 days.</p>
            ) : (
              <ol aria-label="Recent chat" className="well space-y-1 p-3 text-sm">
                {r.data.chat.map((m) => (
                  <ChatLine key={m.id} line={m}>
                    <span className="text-xs text-ink-faint">{m.channel === 'realm' ? 'Realm' : getTown(m.channel.slice('town:'.length) as never).name}</span>
                    {!m.removed && <RemoveLine line={m} label="Remove" />}
                  </ChatLine>
                ))}
              </ol>
            )}
          </div>
          <Activity data={r.data} onLook={look} />
        </article>
      )}
    </section>
  );
}

/** What the player has been doing: the reports they are in, who they have blocked, their latest events and their coin and goods. */
function Activity({ data, onLook }: { data: AdminPlayerResponse; onLook: (id: string) => void }) {
  const { events, ledger, reports, blocks, names } = data;
  const who = (id: string) => <button type="button" className="text-ochre underline" onClick={() => onLook(id)}>{names[id] ?? '(gone)'}</button>;
  return (
    <>
      <Reports reports={reports} onLook={onLook} />
      <p className="text-sm">
        <span className="smallcaps text-ink-dim">Has blocked </span>
        {blocks.length === 0 ? <span className="text-ink-faint">nobody</span> : blocks.map((b, i) => (
          <span key={b.id}>{i > 0 && ', '}<button type="button" className="text-ochre underline" onClick={() => onLook(b.id)}>{b.name}</button></span>
        ))}
      </p>
      <details open>
        <summary className="cursor-pointer smallcaps text-sm text-ink-dim">Latest doings ({events.length}, kept 90 days)</summary>
        <Events events={events} tail={(e) => e.otherId && <> · with {who(e.otherId)}</>} />
      </details>
      <details>
        <summary className="cursor-pointer smallcaps text-sm text-ink-dim">Coin and goods ({ledger.length} latest)</summary>
        {ledger.length === 0 ? <p className="mt-2 text-sm text-ink-faint">Nothing yet.</p> : (
          <ol aria-label="Coin and goods" className="mt-2 space-y-1 text-sm">
            {ledger.map((l) => (
              <li key={l.id} className="grid grid-cols-[6.5rem_5rem_1fr] gap-2">
                <time className="font-mono text-xs leading-5 text-ink-faint">{when(l.at)}</time>
                <span className={`text-right font-mono ${l.delta < 0 ? 'text-blood' : ''}`}>{movement(l)}</span>
                <span>{l.reason} · {l.counterparty.startsWith('PLAYER:') ? who(l.counterparty.slice('PLAYER:'.length)) : COUNTERPARTY[l.counterparty] ?? l.counterparty.replace(/^NPC:/, '')}</span>
              </li>
            ))}
          </ol>
        )}
      </details>
    </>
  );
}

const COUNTERPARTY: Record<string, string> = { SYSTEM: 'the realm', SELF: 'used up', BANK: 'own accounts', MARKET: 'market escrow' };
const movement = (l: AdminLedgerRow) => {
  const sign = l.delta > 0 ? '+' : '−';
  return l.kind === 'coin' ? `${sign}${Math.abs(l.delta)}c${l.account && l.account !== 'purse' ? ` ${l.account}` : ''}` : `${sign}${Math.abs(l.delta)}× ${l.itemKey}`;
};

/** Events, each in the player's own words, with something before or after it. */
function Events<E extends AdminEvent>({ events, lead, tail }: { events: E[]; lead?: (e: E) => ReactNode; tail?: (e: E) => ReactNode }) {
  if (events.length === 0) return <p className="mt-2 text-sm text-ink-faint">Nothing in the last 90 days.</p>;
  return (
    <ol aria-label="Doings" className="mt-2 space-y-1 text-sm">
      {events.map((e) => (
        <li key={e.id} className="grid grid-cols-[6.5rem_1fr] gap-2">
          <time className="font-mono text-xs leading-5 text-ink-faint">{when(e.at)}</time>
          <span>{lead?.(e)}{e.text}{tail?.(e)}</span>
        </li>
      ))}
    </ol>
  );
}

/** Reports, open or resolved (kept 90 days after), each way. */
function Reports({ reports, onLook }: { reports: AdminReportSummary[]; onLook: (id: string) => void }) {
  const who = (id: string, name: string) => <button type="button" className="text-ochre underline" onClick={() => onLook(id)}>{name}</button>;
  return (
    <details open={reports.length > 0 && reports.length <= 5}>
      <summary className="cursor-pointer smallcaps text-sm text-ink-dim">Reports ({reports.length})</summary>
      <ol aria-label="Reports" className="mt-2 space-y-1 text-sm">
        {reports.map((r) => (
          <li key={r.id} className="grid grid-cols-[6.5rem_1fr] gap-2">
            <time className="font-mono text-xs leading-5 text-ink-faint">{when(r.at)}</time>
            <span>
              {who(r.reporterId, r.reporterName)} reported {who(r.targetId, r.targetName)} · {REPORT_REASON_LABELS[r.reason]} ·{' '}
              {r.resolution ? `resolved, ${r.resolution.outcome === 'actioned' ? 'actioned' : 'no action'}${r.resolution.note ? `: ${r.resolution.note}` : ''}` : <span className="text-blood">open</span>}
              {r.line !== null && <span className="block text-ink-faint">“{r.line}”</span>}
              {r.note && <span className="block text-ink-faint">Note: {r.note}</span>}
            </span>
          </li>
        ))}
      </ol>
    </details>
  );
}

/** What has passed between two players: blocks, reports either way, and the events with both in them. Read when opened. */
function Between({ a, b, onLook }: { a: { id: string; name: string }; b: { id: string; name: string }; onLook: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const q = useQuery(adminBetweenQuery(a.id, b.id, open));
  const name = (id: string) => (id === a.id ? a.name : b.name);
  return (
    <details onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="cursor-pointer smallcaps text-sm text-ink-dim">Between {a.name} and {b.name}</summary>
      <div className="mt-2 space-y-2 text-sm">
        {open && q.isPending && <p className="text-ink-faint">Reading…</p>}
        <ErrorNotice error={q.error} />
        {q.data && (
          <>
            <p>
              {q.data.blocks.aBlockedB && `${a.name} has blocked ${b.name}. `}
              {q.data.blocks.bBlockedA && `${b.name} has blocked ${a.name}. `}
              {!q.data.blocks.aBlockedB && !q.data.blocks.bBlockedA && 'Neither has blocked the other.'}
            </p>
            {q.data.reports.length > 0 && <Reports reports={q.data.reports} onLook={onLook} />}
            <h3 className="smallcaps text-ink-dim">Fights, trades and market fills (90 days)</h3>
            <Events events={q.data.events} lead={(e) => <span className="text-ochre">{name(e.of)}: </span>} />
          </>
        )}
      </div>
    </details>
  );
}

const goods = (side: unknown) => {
  const s = side as { coin?: number; items?: { itemKey: string; quantity: number }[] } | null;
  if (!s) return '—';
  return [...(s.coin ? [`${s.coin}c`] : []), ...(s.items ?? []).map((i) => `${i.quantity}× ${i.itemKey}`)].join(', ') || 'nothing';
};

/** Trades accepted between two players on the same device (M5): a sign of one person playing both. */
function Flags({ onLook }: { onLook: (q: string) => void }) {
  const q = useQuery(adminFlagsQuery());
  if (q.isPending) return <p className="text-ink-faint">Reading the flags…</p>;
  if (q.error) return <ErrorNotice error={q.error} />;
  if (q.data.flags.length === 0) return <p className="text-sm text-ink-faint">No trades between players on one device in the last 180 days.</p>;
  const who = (p: { id: string; name: string }) => <button type="button" className="text-ochre underline" onClick={() => onLook(p.id)}>{p.name}</button>;
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-ink-faint">
          <tr><th className="py-1 pr-3 font-normal">When</th><th className="pr-3 font-normal">From</th><th className="pr-3 font-normal">Gave</th><th className="pr-3 font-normal">To</th><th className="font-normal">Wanted</th></tr>
        </thead>
        <tbody>
          {q.data.flags.map((f) => (
            <tr key={f.id} className="border-t border-rule">
              <td className="py-1 pr-3 font-mono text-xs">{when(f.at)}</td>
              <td className="pr-3">{who(f.from)}</td>
              <td className="pr-3">{goods(f.give)}</td>
              <td className="pr-3">{who(f.to)}</td>
              <td>{goods(f.want)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const num = (n: number) => (n === 0 ? 0 : n).toLocaleString('en-GB');
const signed = (n: number) => (n > 0 ? `+${num(n)}` : num(n));

/**
 * The realm's coin by day for the last 30 days, from the stream tally: coin made (faucets) and destroyed
 * (sinks), the change in supply, coin that changed hands between players, and new characters; then the
 * 30 days' flows by reason, largest first.
 * A rough view for spotting trouble; the daily export to Athena is the record.
 */
function Realm() {
  const q = useQuery(adminRealmQuery());
  if (q.isPending) return <p className="text-ink-faint">Counting the realm's coin…</p>;
  if (q.error) return <ErrorNotice error={q.error} />;
  const days = q.data.days;
  let running = 0;
  const rows = days.map((d) => ({ ...d, net: d.faucets + d.sinks, running: (running += d.faucets + d.sinks) }));
  const reasons = new Map<string, number>();
  for (const d of days) for (const [k, v] of Object.entries(d.byReason)) reasons.set(k, (reasons.get(k) ?? 0) + v);
  const ranked = [...reasons].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  const total = (flow: string) => ranked.filter(([k]) => k.startsWith(`${flow}:`)).reduce((n, [, v]) => n + v, 0);
  return (
    <div className="space-y-4">
      <section className="panel overflow-x-auto" aria-label="Coin by day">
        <h2 className="heading mb-2"><Icon name={UI.flows} className="mr-1" />Coin by day</h2>
        <table className="w-full text-sm">
          <thead className="text-left text-ink-faint">
            <tr>
              <th className="py-1 pr-3 font-normal">Day (UTC)</th>
              <th className="pr-3 text-right font-normal">Made</th>
              <th className="pr-3 text-right font-normal">Destroyed</th>
              <th className="pr-3 text-right font-normal">Change</th>
              <th className="pr-3 text-right font-normal">Over 30 days</th>
              <th className="pr-3 text-right font-normal">Changed hands</th>
              <th className="text-right font-normal">New characters</th>
            </tr>
          </thead>
          <tbody>
            {[...rows].reverse().map((d) => (
              <tr key={d.day} className="border-t border-rule font-mono text-xs">
                <td className="py-1 pr-3">{d.day}</td>
                <td className="pr-3 text-right">{num(d.faucets)}</td>
                <td className="pr-3 text-right">{num(-d.sinks)}</td>
                <td className="pr-3 text-right">{signed(d.net)}</td>
                <td className="pr-3 text-right">{signed(d.running)}</td>
                <td className="pr-3 text-right">{num(d.transfers)}</td>
                <td className="text-right">{d.characters}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="panel space-y-2" aria-label="Flows by reason">
        <h2 className="heading">By reason, last 30 days</h2>
        {ranked.length === 0 ? (
          <p className="text-sm text-ink-faint">No coin has been made or destroyed yet.</p>
        ) : (
          <>
            <p className="text-sm text-ink-dim">Made {num(total('faucet'))} · destroyed {num(-total('sink'))} · sinks take {total('faucet') ? Math.round((-total('sink') / total('faucet')) * 100) : 0}% of what is made.</p>
            <table className="w-full text-sm">
              <tbody>
                {ranked.map(([k, v]) => (
                  <tr key={k} className="border-t border-rule">
                    <td className="py-1 pr-3 text-ink-dim">{k.startsWith('faucet:') ? 'Made by' : 'Destroyed by'}</td>
                    <td className="pr-3">{k.slice(k.indexOf(':') + 1)}</td>
                    <td className="text-right font-mono text-xs">{num(Math.abs(v))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </div>
  );
}
